import * as React from 'react';
import { connect } from 'react-redux';
import { PANELS_SET_ROTATION, REMOVE_ROOF_OBSTRUCTIONS, RESET_PANELS_STATE } from 'actions';
import ConfirmationTextField from '__common/components/ConfirmationTextField';
import CustomTooltip from '__common/components/CustomTooltip';
import { InfoOutlined } from '@mui/icons-material';

interface props {
  degrees: number;
  changePanelsRotationDegrees: (degrees: Number, roofId: number) => void;
  panels: panelInState[],
  selectedRoofId: number;
  obstructions: obstructionsState;
}

interface state {
  degrees: string | number;
  isValid: boolean;
}


class PanelsRotation extends React.Component<props, state> {
  constructor(props: props) {
    super(props);
    this.state = {
      isValid: true,
      degrees: props.degrees,
    };
  }

  componentDidUpdate(prevProps: props) {
    if (prevProps.degrees !== this.props.degrees) {
      this.setState({ degrees: this.props.degrees, isValid: true });
    }
  }

  static minSize = 0;
  static maxSize = 45;

  onDegreeChange = (value: number) => {
    if(value >= PanelsRotation.minSize && value <= PanelsRotation.maxSize) {
      this.setState({ ...this.state, degrees: value, isValid: true });
      const degree = parseFloat(Number(value).toFixed(1).replace(/\.?0*$/, ''));
      this.props.changePanelsRotationDegrees(degree, this.props.selectedRoofId);
    }
    else {
      this.setState({ ...this.state, degrees: value, isValid: false });
    }
  }

  getRangeHelper() {
    return `(${PanelsRotation.minSize}-${PanelsRotation.maxSize})`;
  }

  renderError() {
    if (this.state.isValid) {
      return null;
    }

    const errorMessage = `Rotation Angle must be in range ${PanelsRotation.minSize} - ${PanelsRotation.maxSize}`;

    return (
      <div className="input-warning">{errorMessage}</div>
    );
  }

  _hasObstructions = () => {
    const { obstructions: {obstructions}, selectedRoofId } = this.props;
    return obstructions && obstructions[selectedRoofId] && Object.keys(obstructions[selectedRoofId]).length > 0;
  }
  
  handleKeyEvent = (event) => {
    const input = event.target;
    const currentValue = parseFloat(input.value) || 0;
    const max = parseFloat(input.max);
    const min = parseFloat(input.min);

    if (event.key === "ArrowUp" && currentValue >= max) {
      this.setState({ ...this.state, isValid: false });
    }

    if (event.key === "ArrowDown" && currentValue <= min) {
      this.setState({ ...this.state, isValid: false });
    }
  };

  _hasPanels = () => {
    const { panels } = this.props;
    return panels && Array.isArray(panels) && panels.length > 0;
  }

  shouldConfirm = (value: number) => {
    if (value >= PanelsRotation.minSize && value <= PanelsRotation.maxSize) {
      return this._hasPanels() || this._hasObstructions();
    }
    else {
      return false;
    }
  }

  render() {
    const { degrees } = this.state;
    return (
      <div className="desired-table-size rotation-angle-field">
            <div className="input-label">
              Rotation Angle (degrees){' '}
              <CustomTooltip title="The rotation angle is defined as angle between alignment of the array and the building. Additional pressure coefficients are applied when skew angle is greater than 12.5° for the given rotation angle of roof." placement="right">
                <InfoOutlined className="flyout-info-icon" />
              </CustomTooltip>
          </div>
          <ConfirmationTextField
            value={degrees ?? ""}
            onConfirm={value => this.onDegreeChange(value)}
            title="Are you sure you want to change rotation angle?"
            content="Current layout will be lost"
            shouldConfirm={true}
            shouldConfirmCb={(value) => this.shouldConfirm(value)}
            applyOnBlur
            compactConfirm
            textFieldProps={{
              type: 'number',
              fullWidth: false,
              className: 'rotation-angle-input',
              inputProps: {
                step: 0.1,
                min: 0,
                max: 45,
              },
              onKeyDown: this.handleKeyEvent,
            }}
          />
        {this.renderError()}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: Function) => {
  return {
    changePanelsRotationDegrees: (degrees: number, roofId: number) => {
      dispatch(RESET_PANELS_STATE());
      dispatch(PANELS_SET_ROTATION(degrees));
      dispatch(REMOVE_ROOF_OBSTRUCTIONS(roofId));
    }
  };
};

const mapStateToProps = (state: appState) => {
  return {
    degrees: state.background.panelsRotationDegrees,
    panels: state.panels.panels,
    selectedRoofId: state.background.selectedRoofId,
    obstructions: state.obstructions,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PanelsRotation);
